import { useEffect, useState } from 'react';
import { TrendingUp, AlertCircle } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { crmApi, ApiError } from '@solara/api-client';
import type { OpportunityDto } from '@solara/api-client';

const STAGES: Array<OpportunityDto['pipelineStage']> = [
  'DISCOVERY',
  'DEMO',
  'PROPOSAL',
  'NEGOTIATION',
  'CLOSED_WON',
  'CLOSED_LOST',
];

const STAGE_TONES: Record<OpportunityDto['pipelineStage'], 'info' | 'warning' | 'success' | 'danger' | 'neutral'> = {
  DISCOVERY: 'info',
  DEMO: 'info',
  PROPOSAL: 'warning',
  NEGOTIATION: 'warning',
  CLOSED_WON: 'success',
  CLOSED_LOST: 'danger',
};

export default function OpportunitiesKanbanPage() {
  const { t } = useTranslation('p1');
  const [opportunities, setOpportunities] = useState<OpportunityDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      setOpportunities(await crmApi.listOpportunities());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function moveTo(opp: OpportunityDto, stage: OpportunityDto['pipelineStage']) {
    if (stage === 'CLOSED_LOST') {
      const reason = window.prompt(t('crm.lossReasonRequired'));
      if (!reason) return;
      await crmApi.setStage(opp.id, stage, reason);
    } else {
      await crmApi.setStage(opp.id, stage);
    }
    await load();
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('crm.pipelineTitle')}</h1>
        <p className="text-fg-muted text-sm">{t('crm.pipelineSubtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {STAGES.map((stage) => {
          const stageOps = opportunities.filter((o) => o.pipelineStage === stage);
          const value = stageOps.reduce((acc, o) => acc + Number(o.amountMinor) / 100, 0);
          const stageLabel = t(`crm.stages.${stage}`, { defaultValue: stage.replace(/_/g, ' ') });
          return (
            <Card key={stage} className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="h-3 w-3" />
                    {stageLabel}
                  </span>
                  <Badge tone={STAGE_TONES[stage]} className="text-[10px]">
                    {stageOps.length}
                  </Badge>
                </CardTitle>
                <p className="text-[10px] text-fg-muted">
                  {value.toLocaleString()} AED
                </p>
              </CardHeader>
              <CardBody className="space-y-2 text-xs">
                {stageOps.length === 0 ? (
                  <p className="py-3 text-center text-fg-muted">—</p>
                ) : (
                  stageOps.map((opp) => (
                    <div
                      key={opp.id}
                      className="rounded border border-border bg-surface p-2 hover:bg-surface-muted"
                    >
                      <p className="font-medium truncate">{opp.dealName}</p>
                      <p className="text-fg-muted">
                        {(Number(opp.amountMinor) / 100).toLocaleString()} {opp.currency}
                      </p>
                      <p className="text-fg-muted">
                        {opp.probability}% · {t('crm.closeShort')}{' '}
                        {new Date(opp.expectedCloseDate).toLocaleDateString()}
                      </p>
                      {stage !== 'CLOSED_WON' && stage !== 'CLOSED_LOST' && (
                        <div className="mt-2 flex gap-1 flex-wrap">
                          {nextStages(stage).map((next) => (
                            <button
                              key={next}
                              onClick={() => void moveTo(opp, next)}
                              className="rounded bg-brand-100 px-1.5 py-0.5 text-[10px] text-brand-800 hover:bg-brand-200"
                            >
                              → {t(`crm.stages.${next}`, { defaultValue: next.replace(/_/g, ' ') })}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function nextStages(current: OpportunityDto['pipelineStage']): OpportunityDto['pipelineStage'][] {
  const idx = STAGES.indexOf(current);
  if (idx === -1) return [];
  const next = STAGES[idx + 1];
  const list: OpportunityDto['pipelineStage'][] = [];
  if (next && next !== 'CLOSED_LOST') list.push(next);
  list.push('CLOSED_WON', 'CLOSED_LOST');
  return list.filter((s) => s !== current);
}
